import React from "react";

import Grid from "@material-ui/core/Grid";
import { useSelector } from "react-redux";

import { AdminLink } from "components/AdminLink/AdminLink";
import { SubtleBadge } from "components/Badge";
import { FormCollapse } from "components/Form";
import { Row } from "components/Layout";

import { FinanceSubModalSection } from "constants/settings";

import { BankingDetails } from "containers/Settings/Deployment/Finance/BankingDetails";
import { GeneralSettings } from "containers/Settings/Deployment/Finance/General";
import { InsuranceSettings } from "containers/Settings/Deployment/Finance/Insurance";
import { InterestSettings } from "containers/Settings/Deployment/Finance/Interest";
import { InvoiceSettings } from "containers/Settings/Deployment/Finance/Invoice";
import { PaymentPreferencesSection } from "containers/Settings/Deployment/Finance/PaymentPreferences";
import { PaymentSettings } from "containers/Settings/Deployment/Finance/Payments";
import { RCTISettings } from "containers/Settings/Deployment/Finance/RCTI";
import { RetainedProceedsSettings } from "containers/Settings/Deployment/Finance/RetainedProceeds";
import { SellViaExAgentRebatesSection } from "containers/Settings/Deployment/Finance/SellViaExAgentRebates";
import { TradingTermsSection } from "containers/Settings/Deployment/Finance/TradingTerms";
import { TrackingCategoriesSettings } from "containers/Settings/Deployment/Finance/TrackingCategories";
import { VendorCommissionSettings } from "containers/Settings/Deployment/Finance/VendorCommission";
import { XeroLabelSyncSettings } from "containers/Settings/Deployment/Finance/XeroLabelSync";
import {
  DeploymentSettingsModalSection,
  FormColumn,
} from "containers/Settings/Deployment/index";

import { getDefaultBillingSettings } from "selectors";

import {
  useHasSellViaExAgentRebatesPermission,
  useHasTrackingCategoriesPermission,
  useHasTradingTermsPermission,
  useSectionToggle,
} from "hooks";

const header = title => (
  <Row>
    <SubtleBadge>{title}</SubtleBadge>
  </Row>
);

export function FinanceSection(props) {
  const { sectionHelper, ns } = props;

  const modalSection = DeploymentSettingsModalSection.FINANCE_REPORTS;
  const [section, setSection] = sectionHelper;

  const isOpen = section === modalSection;
  const onToggle = () => setSection(modalSection);

  const subSectionHelper = useSectionToggle(FinanceSubModalSection.GENERAL);

  const hasTradingTermsFeature = useHasTradingTermsPermission();
  const hasSellViaExAgentRebatesFeature =
    useHasSellViaExAgentRebatesPermission();
  const hasTrackingCategoriesFeature = useHasTrackingCategoriesPermission();
  const billingSettings = useSelector(getDefaultBillingSettings);
  const adminLink = (
    <AdminLink model="billingsettings" cbid={billingSettings?.id} />
  );

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={header(<>Finance{adminLink}</>)}
      dataTour="finance"
    >
      <Grid container spacing={2}>
        <FormColumn>
          <GeneralSettings sectionHelper={subSectionHelper} namespace={ns} />
          <RCTISettings sectionHelper={subSectionHelper} namespace={ns} />
          <InvoiceSettings sectionHelper={subSectionHelper} namespace={ns} />
          <RetainedProceedsSettings
            sectionHelper={subSectionHelper}
            namespace={ns}
          />
          <PaymentSettings sectionHelper={subSectionHelper} namespace={ns} />
          <BankingDetails sectionHelper={subSectionHelper} namespace={ns} />
          <PaymentPreferencesSection
            sectionHelper={subSectionHelper}
            namespace={ns}
          />
          <InsuranceSettings sectionHelper={subSectionHelper} namespace={ns} />

          {hasTradingTermsFeature && (
            <TradingTermsSection
              sectionHelper={subSectionHelper}
              namespace={ns}
            />
          )}
          {hasSellViaExAgentRebatesFeature && (
            <SellViaExAgentRebatesSection
              sectionHelper={subSectionHelper}
              namespace={ns}
            />
          )}
          {hasTrackingCategoriesFeature && (
            <TrackingCategoriesSettings
              sectionHelper={subSectionHelper}
              namespace={ns}
            />
          )}
          <VendorCommissionSettings
            sectionHelper={subSectionHelper}
            namespace={ns}
          />
          <InterestSettings sectionHelper={subSectionHelper} namespace={ns} />
          <XeroLabelSyncSettings
            sectionHelper={subSectionHelper}
            namespace={ns}
          />
        </FormColumn>
      </Grid>
    </FormCollapse>
  );
}
